import { useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/dexie.js';
import { buscarEntre } from '../lib/catalogo.js';
import { fotoDeArchivo, ponerFoto } from '../lib/fotos.js';
import { codigosParaBuscar, sePuedenSugerir } from '../lib/fotosSugeridas.js';
import { useLectorUsb } from '../lib/lector-usb.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { Miniatura } from '../ui/Miniatura.js';
import { CamaraFoto, useCamaraEnLaPagina } from '../ui/CamaraFoto.js';
import { FotosSugeridas } from '../ui/FotosSugeridas.js';
import { useNombreProducto } from '../i18n/productos.js';
import { useT, type Clave } from '../i18n/index.js';
import { etiquetaCategoriaProducto } from '../i18n/dominio.js';

type Filtro = 'sinFoto' | 'todos';

/**
 * Photographing a whole catalogue in one sitting (the owner, 2026-10-20: «a tool much like the
 * missing prices and missing costs tool for MISSING PHOTOS for each product»).
 *
 * A photo is how a product is found at a glance at the till, and the only way to give one to a
 * product already made was its own page: open it, take the photo, back, find the next. On
 * 2026-10-20 the shop had 216 of its 323 products on sale with none, and every photo it had was
 * taken when its product was first made.
 *
 * So, as «Precios/Costos» lists what still has no price, this lists what still has no photo, a row
 * each, and a row's «Tomar foto» opens the camera and puts the photo on the product the moment it is
 * accepted; its «Galería», a picture already on the phone, such as one the supplier sent. A row done
 * stays where it is, «Foto guardada», until another view is picked, so a photo that came out wrong
 * is taken again in place.
 *
 * A tab of «Productos», for whoever can change a product's photo on its page: the owner, and a
 * cashier, who is as likely to be the one at the shelves. The search finds a product by its code
 * too, and a USB scanner's code goes into it wherever the cursor was, as on «Productos».
 *
 * And «Buscar en internet» (the owner, 2026-10-22: «a photo suggester that looks for photos on the internet of
 * that particular product as a suggested picture we can use for that product's thumbnail»): the
 * photos the shop's server finds for it by its codes and its name, to pick one (`FotosSugeridas`),
 * which goes on whole, white round it. Not in the practice shop, which asks no server.
 */
export function Fotos() {
  const t = useT();
  const nombre = useNombreProducto();
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);

  /** Missing a photo, or every product. */
  const [filtro, setFiltro] = useState<Filtro>('sinFoto');
  const [consulta, setConsulta] = useState('');
  /**
   * The products photographed on this visit. They stay under «Sin foto» once they have one, until
   * another view is picked, so that a photo is taken again where it was taken.
   */
  const [hechas, setHechas] = useState<ReadonlySet<string>>(() => new Set());
  /** The product whose photo is being made small and saved: one at a time, as there is one camera. */
  const [trabajando, setTrabajando] = useState<string | null>(null);
  /** The product whose picture could not be used, said on its row. */
  const [noSePudo, setNoSePudo] = useState<string | null>(null);
  /** The camera and the gallery, a file input each for the screen, and the product one was opened for. */
  const camara = useRef<HTMLInputElement>(null);
  const galeria = useRef<HTMLInputElement>(null);
  const para = useRef<string | null>(null);
  /** On a computer with a camera, the camera is the page's (`CamaraFoto`), and this the product it is open for. */
  const enLaPagina = useCamaraEnLaPagina();
  const [fotografiando, setFotografiando] = useState<string | null>(null);
  /** The product whose photos from the internet are open. */
  const [sugiriendo, setSugiriendo] = useState<string | null>(null);
  const conInternet = sePuedenSugerir();
  /** The search box, where whatever a USB scanner types on this screen is read (`useLectorUsb`). */
  const caja = useRef<HTMLInputElement>(null);
  useLectorUsb(caja, setConsulta);

  /** Progress is over the whole catalogue on sale, not over what the filter happens to be showing. */
  const activos = useMemo(() => productos.filter((p) => p.activo), [productos]);
  const total = activos.length;
  const sinFoto = activos.filter((p) => !p.foto).length;
  /**
   * One being photographed stays too: its photo lands in the books a moment before it is counted
   * among those done on this visit.
   */
  const filas = useMemo(
    () =>
      buscarEntre(
        filtro === 'todos' ? activos : activos.filter((p) => !p.foto || hechas.has(p.id) || p.id === trabajando),
        consulta,
        Infinity,
        nombre,
        unidades,
      ),
    [activos, filtro, hechas, trabajando, consulta, nombre, unidades],
  );
  /** Each view of what is lacking says how many lack it, as «Sin precio (12)» does. */
  const filtros: readonly { vista: Filtro; etiqueta: Clave; cuantos?: number }[] = [
    { vista: 'sinFoto', etiqueta: 'fotos.sinFoto', cuantos: sinFoto },
    { vista: 'todos', etiqueta: 'fotos.todos' },
  ];

  /** Another view: what was photographed on this one goes where it now belongs. */
  function verOtra(v: Filtro) {
    setFiltro(v);
    setHechas(new Set());
    setNoSePudo(null);
  }

  function elegir(productoId: string, de: 'camara' | 'galeria' | 'internet') {
    if (trabajando) return;
    setNoSePudo(null);
    if (de === 'internet') {
      setSugiriendo(productoId);
      return;
    }
    if (de === 'camara' && enLaPagina) {
      setFotografiando(productoId);
      return;
    }
    para.current = productoId;
    (de === 'camara' ? camara : galeria).current?.click();
  }

  /** A picture for a product, from a camera, a file or the internet: made its photo, and put on it; whole, if `entera`. */
  async function usar(productoId: string, archivo: Blob, entera = false) {
    setTrabajando(productoId);
    try {
      await ponerFoto(productoId, await fotoDeArchivo(archivo, { entera }));
      setHechas((h) => new Set(h).add(productoId));
    } catch (e) {
      // A file the browser cannot decode: a HEIC on a laptop, a PDF picked by mistake.
      console.warn('[foto]', e);
      setNoSePudo(productoId);
    } finally {
      setTrabajando(null);
    }
  }

  async function alElegir(entrada: HTMLInputElement) {
    const archivo = entrada.files?.[0];
    const productoId = para.current;
    para.current = null;
    if (archivo && productoId) await usar(productoId, archivo);
    // Cleared so that the same file picked again still counts as a change.
    entrada.value = '';
  }

  const enCamara = fotografiando ? productos.find((p) => p.id === fotografiando) : undefined;
  const enInternet = sugiriendo ? productos.find((p) => p.id === sugiriendo) : undefined;

  if (total === 0) {
    return (
      <Pagina titulo={t('fotos.titulo')}>
        <Vacio mensaje={t('fotos.nadaQueMostrar')}>
          <Link to="/productos/semilla" className="btn-primario px-5">
            <Ico icono="semilla" />
            {t('productos.empezarCatalogo')}
          </Link>
        </Vacio>
      </Pagina>
    );
  }

  return (
    <Pagina titulo={t('fotos.titulo')} subtitulo={t('fotos.subtitulo', { hechos: total - sinFoto, total })}>
      <p className="mb-4 rounded-xl bg-slate-900 px-4 py-3 text-xs text-slate-400">{t('fotos.explicacion')}</p>

      {/* How far along, so the job has a visible end. */}
      <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-800">
        <div
          className="h-full rounded-full bg-marca-500 transition-all"
          style={{ width: `${Math.round(((total - sinFoto) / total) * 100)}%` }}
        />
      </div>

      {/* Under the search on a phone, where «Missing Photos (216)» beside it leaves the box no room. */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <input
          ref={caja}
          className="campo min-w-0 flex-1"
          placeholder={t('fotos.buscar')}
          value={consulta}
          onChange={(e) => setConsulta(e.target.value)}
          autoComplete="off"
          inputMode="search"
        />
        <div className="flex shrink-0 gap-1 rounded-xl bg-slate-800 p-1">
          {filtros.map(({ vista, etiqueta, cuantos }) => (
            <button
              key={vista}
              type="button"
              onClick={() => verOtra(vista)}
              aria-pressed={filtro === vista}
              data-fotos-filtro={vista}
              data-sin-icono="pestana"
              // Too narrow for both on one line, the count goes under its name, never into it.
              className={`min-h-10 flex-auto rounded-lg px-2.5 py-1 text-sm leading-tight transition max-[359px]:px-2 ${
                filtro === vista ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`}
            >
              <span className="whitespace-nowrap">{t(etiqueta)}</span>
              {cuantos !== undefined && (
                <>
                  {' '}
                  <span className="tabular whitespace-nowrap">({cuantos})</span>
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      {filas.length === 0 ? (
        consulta.trim() ? (
          <Vacio mensaje={t(filtro === 'sinFoto' ? 'fotos.ningunoSinFoto' : 'fotos.ninguno')} icono="productos" />
        ) : (
          <Vacio mensaje={t('fotos.todosConFoto')}>
            <Link to="/" className="btn-primario px-5">
              <Ico icono="listo" />
              {t('fotos.seguir')}
            </Link>
          </Vacio>
        )
      ) : (
        <ul className="flex flex-col gap-2">
          {filas.map((p) => {
            const guardada = hechas.has(p.id) && Boolean(p.foto);
            const preparando = trabajando === p.id;
            return (
              <li
                key={p.id}
                // The button under the name on a phone, beside it where there is room.
                className="tarjeta flex flex-wrap items-center gap-3 p-3"
                data-fotos-producto={p.id}
                data-fotos-estado={guardada ? 'guardada' : p.foto ? 'con-foto' : 'sin-foto'}
              >
                <Miniatura producto={p} tamano="lg" />
                <div className="min-w-0 flex-1 basis-40">
                  <p className="truncate font-medium">{nombre(p)}</p>
                  {guardada ? (
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-teal-300">
                      <Ico icono="listo" className="size-3.5 shrink-0" />
                      <span className="truncate">{t('fotos.guardada')}</span>
                    </p>
                  ) : (
                    <p className="mt-0.5 truncate text-xs text-slate-500">{etiquetaCategoriaProducto(p.categoria)}</p>
                  )}
                  {noSePudo === p.id && <p className="mt-1 text-xs text-rose-400">{t('foto.noSePudo')}</p>}
                </div>
                <div className="flex w-full flex-wrap gap-2 sm:w-auto">
                  <button
                    type="button"
                    className="btn-secundario h-11 min-h-0 flex-1 gap-1.5 px-4 text-sm whitespace-nowrap sm:flex-none"
                    disabled={trabajando !== null}
                    onClick={() => elegir(p.id, 'camara')}
                    aria-label={t(p.foto ? 'fotos.cambiarDe' : 'fotos.tomarDe', { producto: nombre(p) })}
                    data-fotos-tomar={p.id}
                  >
                    <Ico icono="foto" className="size-4" />
                    {t(preparando ? 'foto.preparando' : p.foto ? 'foto.cambiar' : 'foto.tomar')}
                  </button>
                  {/* Its word where there is room for it beside «Cambiar foto»; under 360 px the pictures alone. */}
                  <button
                    type="button"
                    className="btn-secundario h-11 min-h-0 min-w-11 gap-1.5 px-3 text-sm whitespace-nowrap"
                    disabled={trabajando !== null}
                    onClick={() => elegir(p.id, 'galeria')}
                    aria-label={t('fotos.galeriaDe', { producto: nombre(p) })}
                    data-fotos-galeria={p.id}
                  >
                    <Ico icono="galeria" className="size-4" />
                    <span className="max-[359px]:hidden">{t('foto.galeria')}</span>
                  </button>
                  {/*
                    A line of its own under the two on a phone, which has room for two and their words,
                    not three: there the three squeezed each other's pictures to nothing (2026-10-22).
                  */}
                  {conInternet && (
                    <button
                      type="button"
                      className="btn-secundario h-11 min-h-0 gap-1.5 px-3 text-sm whitespace-nowrap max-sm:basis-full"
                      disabled={trabajando !== null}
                      onClick={() => elegir(p.id, 'internet')}
                      aria-label={t('fotos.internetDe', { producto: nombre(p) })}
                      data-fotos-internet={p.id}
                    >
                      <Ico icono="internet" className="size-4 shrink-0" />
                      {t('foto.internet')}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/*
        «Tomar foto» opens the camera itself, the back one, and what it takes comes straight here (the
        owner, 2026-10-20: «on mobile can we make it so that i can just take the photo directly from
        the camera and it uploads instead of having to take a photo then select it in the photo
        gallery»): without `capture`, an Android phone opened its gallery. «Galería» opens that, for
        a picture already on the phone. A computer ignores `capture`: with a camera, «Tomar foto»
        opens it in the page instead (`CamaraFoto`), and with none, a file.
      */}
      <input
        ref={camara}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => void alElegir(e.currentTarget)}
      />
      <input ref={galeria} type="file" accept="image/*" className="hidden" onChange={(e) => void alElegir(e.currentTarget)} />

      {enInternet && (
        <FotosSugeridas
          producto={nombre(enInternet)}
          nombre={enInternet.nombre}
          codigos={codigosParaBuscar(enInternet, unidades)}
          onUsar={(foto) => {
            setSugiriendo(null);
            void usar(enInternet.id, foto, true);
          }}
          onCerrar={() => setSugiriendo(null)}
        />
      )}

      {enCamara && (
        <CamaraFoto
          titulo={t('foto.camara.titulo', { producto: nombre(enCamara) })}
          onTomada={(foto) => {
            setFotografiando(null);
            void usar(enCamara.id, foto);
          }}
          onCerrar={() => setFotografiando(null)}
        />
      )}
    </Pagina>
  );
}
